/*
 * Unsaved changes and Auto-Save (owner, 2026-10-24).
 *
 * Every form is watched: once something in it is changed (typed, picked, ticked, drawn), leaving the page stops first.
 * A link within the site (wire:navigate) asks in the site's dialog (Leave or Stay: dialog-unsaved, layout/dialogs); a
 * reload, closing the tab or a link elsewhere gets the browser's own question. Submitting a form counts as saving it;
 * it's changed again if the server sends back errors (Livewire forms), or when edited again.
 *
 * Auto-Save (owner, 2026-10-24; always on since the preference went): a form that saves something (<form
 * data-autosave>: details, settings, profiles; never sign-in, messages, payments, an email or phone change, nor the
 * Theme, which previews until Save) submits
 * itself when the visitor leaves a field they changed (owner, 2026-10-24), or stops typing long text (a textarea, the
 * editor) for 3 seconds, and at once after a pick or a tick; leaving the page saves first and goes on (it asks only if
 * the save brings back errors). Auto-saves are quiet: no toast (errors still show), and a line in place of the Save
 * button (components/button/save) says how it's going; the button comes back when a save fails.
 *
 * Inside a form, data-unsaved="off" leaves a field (a dropdown's search box) or a whole form (a finder) unwatched. The
 * site's own fields that keep their value in Alpine (dropdown, date, signature, editor) tell it with a "form:changed"
 * event, changed(el) below; the editor, changed as it's typed in, says so (typing) and saves when it's left too.
 */

import { Livewire } from '../../vendor/livewire/livewire/dist/livewire.esm';

const changes = new Map();   // form => its edit count (0: saved)
const sending = new Map();   // form => what's on its way: { count, auto, fields }, kept here (a morph drops attributes)
const fields = new Map();    // form => the fields changed since it was sent (their frames: [data-field])
const timers = new Map();

/** A site field changed by the visitor (not by the server): from its root element; typing: saved once it's left */
export const changed = (el, typing = false) => el.dispatchEvent(new CustomEvent('form:changed', { bubbles: true, detail: { typing } }));

const unsaved = () => [...changes].some(([form, count]) => count && form.isConnected);
const saving = () => [...sending.keys()].some((form) => form.isConnected);
const auto = (form) => form.hasAttribute('data-autosave');

function edited(event) {
    const form = event.target.closest?.('form');
    if (!form || event.target.type === 'search' || event.target.closest('[data-unsaved="off"]')) return;

    changes.set(form, (changes.get(form) ?? 0) + 1);
    status(form, 'changed');
    const field = event.target.closest('[data-field]');
    if (field && form.hasAttribute('data-autosave')) {
        fields.set(form, (fields.get(form) ?? new Set()).add(field));
        mark(field, 'changed');
    }

    // A pick, a tick, a date or a signature saves at once; typed text once its field is left (below), and long text
    // (a textarea, the editor) also once its typing has stopped for 3 seconds. Not a short field: a pause in the middle
    // of "pat@gmail.com" would save "pat@gmail.co"
    const typing = event.type === 'input' || event.detail?.typing
        || (event.type === 'change' && !['checkbox', 'radio', 'select-one', 'select-multiple'].includes(event.target.type));
    if (!typing) save(form);
    else if (event.target.tagName === 'TEXTAREA' || event.detail?.typing) save(form, 3000);
}

['input', 'change', 'form:changed'].forEach((type) => document.addEventListener(type, edited, true));

// Failing the browser's own checks: not saved
document.addEventListener('invalid', (event) => {
    const form = event.target.closest?.('form[data-autosave]');
    const field = event.target.closest('[data-field]');
    if (!form) return;
    field && mark(field, 'error');
    status(form, 'error');
}, true);

// Leaving a field (to the next one, or anywhere else) saves what was typed in it; not when it's only a dropdown's
// search box being opened, the pick will
document.addEventListener('focusout', (event) => {
    const form = event.target.closest?.('form');
    const to = event.relatedTarget;
    if (!form || !changes.get(form) || (to && form.contains(to) && to.closest('[data-unsaved="off"]'))) return;
    save(form);
}, true);

/** Auto-Save: submits a changed form (a moment later, so a pick that also leaves a field saves once) */
function save(form, after = 150) {
    if (!auto(form)) return;
    clearTimeout(timers.get(form));
    timers.set(form, setTimeout(() => submit(form), after));
}

function submit(form) {
    clearTimeout(timers.get(form));
    if (!form.isConnected || !changes.get(form)) return;
    // Not while the browser's own checks fail (a required field emptied): it would pull the visitor back to it
    if (!form.checkValidity()) return;
    form.requestSubmit();
    sending.has(form) && (sending.get(form).auto = true);
}

/** The form's save state, for screen readers (components/button/save); each field shows its own (mark) */
const STATES = {
    saved: 'All changes saved',
    changed: 'Unsaved changes',
    saving: 'Saving…',
    error: 'Not saved: check the fields',
    failed: "Not saved: the site couldn't be reached",
};

function status(form, state) {
    const line = form.querySelector('[data-save-status]');
    if (!line || line.dataset.state === state) return;
    line.dataset.state = state;
    line.textContent = STATES[state];
}

/** A field's own state, at the top right of its label (components/input/field): "Saved ✓" fades after a moment */
const MARKS = { changed: 'Unsaved', saving: 'Saving…', saved: 'Saved ✓', error: 'Not saved', failed: 'Not saved' };

function mark(field, state) {
    const label = field.querySelector('[data-field-status]');
    if (!label) return;
    clearTimeout(label.fade);
    label.dataset.state = state;
    label.textContent = MARKS[state] ?? '';
    if (state === 'saved') label.fade = setTimeout(() => mark(field, ''), 3000);
}

/** The sent ones back: saved, or not (and so changed again) */
function back(form, state) {
    const sent = sending.get(form);
    sending.delete(form);
    if (state !== 'saved') {
        changes.set(form, (changes.get(form) ?? 0) + (sent.count || 1));
        sent.fields.forEach((field) => fields.set(form, (fields.get(form) ?? new Set()).add(field)));
    }
    sent.fields.forEach((field) => {
        // Changed again while it was on its way: still unsaved
        if (state !== 'saved' || !fields.get(form)?.has(field)) mark(field, state);
    });
    status(form, state === 'saved' && changes.get(form) ? 'changed' : state);
}

window.unsaved = {
    /** An Auto-Save on its way back: close-on dialogs stay open (components/dialog) */
    auto: false,

    /** Leave anyway (the dialog's Leave) */
    leave() {
        changes.clear();
        pending && Livewire.navigate(String(pending));
        pending = null;
    },
};

// Submitted: saved, unless the server answers with errors (below). What the visitor sent is remembered, so an edit
// made while it was on its way still counts
document.addEventListener('submit', (event) => {
    const form = event.target;
    clearTimeout(timers.get(form));
    const sent = fields.get(form) ?? new Set();
    sending.set(form, { count: changes.get(form) ?? 0, auto: false, fields: sent });
    fields.delete(form);
    changes.set(form, 0);
    status(form, 'saving');
    sent.forEach((field) => mark(field, 'saving'));
}, true);

Livewire.hook('commit', ({ component, succeed, fail }) => {
    // The forms this request carries: sent and not yet taken by another
    const sent = [...sending].filter(([form, send]) => !send.commit && component.el.contains(form)).map(([form]) => form);
    if (!sent.length) return;
    sent.forEach((form) => sending.get(form).commit = true);

    // Saved by Auto-Save: what the save would do to close things (close-on dialogs) waits, the visitor still in it
    queueMicrotask(() => sent.some((form) => sending.get(form)?.auto) && (window.unsaved.auto = true));

    succeed(({ snapshot }) => {
        // Livewire 4 gives the snapshot as an object (3 gave its JSON)
        const errors = (typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot)?.memo?.errors ?? [];
        sent.forEach((form) => back(form, Object.keys(errors).length ? 'error' : 'saved'));
        setTimeout(() => window.unsaved.auto = false);
        settled();
    });
    // Not saved at all (offline, a server error): as it was
    fail(() => {
        sent.forEach((form) => back(form, 'failed'));
        window.unsaved.auto = false;
        settled();
    });
});

// Leaving the site, reloading or closing: the browser asks
window.addEventListener('beforeunload', (event) => {
    if (unsaved()) {
        event.preventDefault();
        event.returnValue = '';
    }
});

// A link within the site (going back and forward through history isn't stopped): with Auto-Save, what's changed is
// saved and the link followed once it is; the site's dialog asks if that can't be (errors, a form that doesn't
// auto-save)
let pending = null;
let going = false;
document.addEventListener('livewire:navigate', (event) => {
    if (event.detail?.history || (!unsaved() && !saving())) return;
    event.preventDefault();
    pending = event.detail.url;

    const forms = [...changes].filter(([form, count]) => count && form.isConnected).map(([form]) => form);
    if (forms.every(auto)) {
        going = true;
        forms.forEach(submit);
        settled();
    } else {
        window.dispatchEvent(new CustomEvent('dialog-unsaved', { detail: 'open' }));
    }
});

// Leaving after an Auto-Save: once nothing is on its way, go on if it all saved, else ask
function settled() {
    if (!going || saving()) return;
    going = false;
    unsaved() ? window.dispatchEvent(new CustomEvent('dialog-unsaved', { detail: 'open' })) : window.unsaved.leave();
}


// A page left behind starts over
document.addEventListener('livewire:navigated', () => {
    changes.clear();
    sending.clear();
    fields.clear();
    timers.forEach(clearTimeout);
    timers.clear();
});
